import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, MapPin } from 'lucide-react';
import { hrAttendanceRowSchema, hrClockInQrPayloadSchema } from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const REDIRECT_DELAY_MS = 2500;

export default function HRQrClockIn() {
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);
  const { user, isAuthenticated, isLoading } = useAuth();
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();

  const tokenId = searchParams.get('t');
  const totpCode = searchParams.get('c');
  const storeId = searchParams.get('s');
  const dashboardUrl = tenantSlug ? `/${tenantSlug}/dashboard` : '/';

  const [status, setStatus] = useState<'ready' | 'gps' | 'clocking' | 'success' | 'error'>('ready');
  const [message, setMessage] = useState('');
  const [subMessage, setSubMessage] = useState('');
  const [storeName, setStoreName] = useState('');
  const [clockTime, setClockTime] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const redirectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRedirectTimers = useCallback(() => {
    if (redirectIntervalRef.current !== null) {
      clearInterval(redirectIntervalRef.current);
      redirectIntervalRef.current = null;
    }
    if (redirectTimeoutRef.current !== null) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, []);

  const goToDashboard = useCallback(() => {
    clearRedirectTimers();
    window.location.href = dashboardUrl;
  }, [dashboardUrl, clearRedirectTimers]);

  const scheduleRedirect = useCallback(() => {
    clearRedirectTimers();
    setRedirectCountdown(Math.ceil(REDIRECT_DELAY_MS / 1000));
    redirectIntervalRef.current = setInterval(() => {
      setRedirectCountdown((s) => (s === null || s <= 1 ? 0 : s - 1));
    }, 1000);
    redirectTimeoutRef.current = setTimeout(() => {
      clearRedirectTimers();
      window.location.href = dashboardUrl;
    }, REDIRECT_DELAY_MS);
  }, [dashboardUrl, clearRedirectTimers]);

  useEffect(() => clearRedirectTimers, [clearRedirectTimers]);

  const doClockIn = useCallback(async () => {
    if (hasRun.current || !isAuthenticated || !user || user.tenantSlug !== tenantSlug) return;
    const payload = hrClockInQrPayloadSchema.safeParse({ token_id: tokenId, totp_code: totpCode, store_id: storeId });
    if (!payload.success) {
      setStatus('error');
      setMessage('QR non valido');
      setSubMessage('Scansiona un nuovo QR dalla stazione di sede.');
      return;
    }
    hasRun.current = true;
    setStatus('gps');
    setMessage('Acquisizione posizione GPS...');

    let latitude: number | undefined;
    let longitude: number | undefined;

    try {
      if ('geolocation' in navigator) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 15000,
              maximumAge: 0,
            });
          });
          latitude = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch {
        }
      }
    } catch {
    }

    setStatus('clocking');
    setMessage('Registrazione timbratura...');

    try {
      const res = await apiClient.post('/hr/attendance/clock-in', {
        qr_code: JSON.stringify(payload.data),
        latitude,
        longitude,
      });
      const record = hrAttendanceRowSchema.safeParse(res.data);
      if (!res.success || !record.success || record.data.user_id !== user.id ||
          record.data.tenant_id !== user.tenantId || record.data.store_id !== storeId ||
          record.data.clock_in_method !== 'qr_code') {
        throw new Error('Risposta non valida dal server: timbratura non confermata');
      }
      setStatus('success');
      setMessage('Timbratura registrata!');
      setStoreName(record.data.store_name || '');
      setClockTime(new Date(record.data.clock_in).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }));
      scheduleRedirect();
    } catch (e) {
      setStatus('error');
      setMessage(getErrorMessage(e) || 'Timbratura non confermata');
      setSubMessage('Controlla lo stato delle presenze prima di riprovare. Se il QR è scaduto, scansionalo di nuovo.');
    }
  }, [isAuthenticated, user, tenantSlug, storeId, tokenId, totpCode, scheduleRedirect]);

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      background: status === 'success'
        ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
        : status === 'error'
          ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
          : 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        padding: 32,
        textAlign: 'center',
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 24, color: '#1e293b' }}>
          Timbratura QR
        </h2>

        {status === 'ready' && (
          <div className="space-y-4">
            {isLoading ? <p>Verifica accesso...</p> : !isAuthenticated || !user ? (
              <>
                <p>Accedi con il tuo account, poi scansiona un nuovo QR dalla stazione di sede.</p>
                <Button asChild><Link to={`/${tenantSlug}/login`}>Accedi</Link></Button>
              </>
            ) : user.tenantSlug !== tenantSlug ? (
              <p role="alert">Questo QR appartiene a un’altra organizzazione. Accedi con l’account corretto e scansiona un nuovo QR.</p>
            ) : !hrClockInQrPayloadSchema.safeParse({ token_id: tokenId, totp_code: totpCode, store_id: storeId }).success ? (
              <p role="alert">QR non valido. Scansiona un nuovo QR dalla stazione di sede.</p>
            ) : (
              <>
                <p>Stai timbrando come <strong>{user.firstName} {user.lastName}</strong>.</p>
                <Button onClick={() => void doClockIn()}>Conferma ingresso</Button>
              </>
            )}
          </div>
        )}
        {(status === 'gps' || status === 'clocking') && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Loader2 style={{ width: 40, height: 40, color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 14 }}>
              {status === 'gps' && <MapPin style={{ width: 16, height: 16 }} />}
              <span>{message}</span>
            </div>
            <div style={{ width: '60%', height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                background: '#3b82f6',
                borderRadius: 2,
                animation: 'progress 2s ease-in-out infinite',
              }} />
            </div>
          </div>
        )}

        {status === 'success' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'popIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
            }}>
              <CheckCircle2 style={{ width: 52, height: 52, color: '#22c55e' }} />
            </div>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#15803d', margin: 0 }}>{message}</p>
            {clockTime && (
              <p style={{ fontSize: 32, fontWeight: 800, color: '#166534', margin: '4px 0', fontFamily: 'monospace' }}>
                {clockTime}
              </p>
            )}
            {storeName && (
              <p style={{ fontSize: 13, color: '#16a34a', margin: 0 }}>
                📍 {storeName}
              </p>
            )}
            {redirectCountdown !== null && redirectCountdown > 0 && (
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
                Ritorno alla dashboard in {redirectCountdown}s…
              </p>
            )}
            <button
              onClick={goToDashboard}
              style={{
                marginTop: 12,
                padding: '12px 32px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Vai alla dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'popIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
            }}>
              <XCircle style={{ width: 52, height: 52, color: '#ef4444' }} />
            </div>
            <p style={{ fontSize: 18, fontWeight: 600, color: '#dc2626', margin: 0 }}>{message}</p>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{subMessage}</p>
            <button
              onClick={goToDashboard}
              style={{
                marginTop: 16,
                padding: '10px 24px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Controlla presenze
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes popIn { 0% { transform: scale(0); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes progress { 0% { width: 0%; } 50% { width: 80%; } 100% { width: 100%; } }
      `}</style>
    </div>
  );
}
